/**
 * Raíz de la aplicación.
 *
 * El orden importa y es parte de la protección: PRIMERO se pregunta quién es el
 * usuario y solo después se pide nada más. Sin sesión no se llega a solicitar
 * `/api/config`, y por tanto la interfaz nunca llega a tener en memoria una
 * cifra contable — aunque el backend ya lo impediría por su cuenta.
 *
 * Después, `/api/config` alimenta toda la interfaz: ambiente, estado del
 * mapping, catálogo de reportes y token CSRF.
 */

import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ApiError, api } from "./api/client";
import { useAsync } from "./api/useAsync";
import { aplicarApariencia } from "./components/apariencia";
import { Layout } from "./components/Layout";
import { SessionPending } from "./components/auth";
import { JournalVoucherPage } from "./pages/JournalVoucher";
import { recordarSesion, sesionConocida } from "./components/sessionMark";
import { ErrorState, Loading } from "./components/States";
import { Login } from "./pages/Login";
import {
  Audit,
  AuditComposition,
  AuditDivergences,
  AuditRules,
} from "./pages/Audit";
import { AuditClassification } from "./pages/AuditClassification";
import { Dashboard } from "./pages/Dashboard";
import { Exceptions } from "./pages/Exceptions";
import { Report } from "./pages/Report";
import { Settings } from "./pages/Settings";
import { RunDetail } from "./pages/RunDetail";
import { Runs } from "./pages/Runs";
import { Statement } from "./pages/Statement";
import { AccountingPolicy } from "./pages/closing/AccountingPolicy";
import {
  CashFlowDetail,
  CashFlowOfficial,
  CashFlowReconciliation,
  CashFlowStatement,
} from "./pages/closing/CashFlowStatement";
import { ClosingLayout } from "./pages/closing/ClosingLayout";
import { ClosingNotes } from "./pages/closing/ClosingNotes";
import { ClosingOverview } from "./pages/closing/ClosingOverview";
import { EquityDetail, EquityMatrix, EquityOfficial } from "./pages/closing/EquityMatrix";
import { FinancialDashboard } from "./pages/closing/FinancialDashboard";
import { FormalStatement } from "./pages/closing/FormalStatement";
import { MonthlyDetail } from "./pages/closing/MonthlyDetail";
import { Ratios } from "./pages/closing/Ratios";
import { StatementAnnex } from "./pages/closing/StatementAnnex";
import type { Config, SessionInfo } from "./api/types";

export default function App() {
  const { data: session, loading: checking } = useAsync(() => api.session(), []);

  /* Whether this browser has been inside before. Read ONCE, before the check
     resolves, because its only job is to choose what to draw during the wait —
     the application loading, or the gate. Read after, it would always say
     "yes" and never help.

     It is a comfort, not a permission: the server decides, and two lines below
     an unauthenticated answer still lands on the gate. */
  const [regresa] = useState(sesionConocida);

  /* The mark follows the answer, in both directions. Clearing it on a failed
     check is what stops a signed-out browser from waiting on an application
     frame for a session it no longer has. */
  useEffect(() => {
    if (checking) return;
    recordarSesion(Boolean(session?.authenticated && !session.degraded));
  }, [checking, session]);

  /* The saved palette, before anything is drawn. The gate asks for it too —
     the endpoint is public — because a customized platform behind a
     factory-colored door reads as two different products.

     Deliberately not awaited, and its failure deliberately ignored: the shell
     falls back to the stylesheet defaults, and an unreachable color must never
     be what keeps somebody from signing in. */
  useEffect(() => {
    let cancelled = false;
    void api
      .brandAppearance()
      .then((appearance) => {
        if (!cancelled) aplicarApariencia(appearance);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /* Nothing is painted for the first fraction of a second. On a reload the
     session check answers well inside that window, so the application appears
     without the gate flashing in between — which is what someone reloading a
     page they were already inside was seeing. */
  if (checking) return <SessionPending what="Comprobando su sesión." returning={regresa} />;
  // Ante cualquier duda sobre la sesión se muestra el portón, no la aplicación.
  if (!session || !session.authenticated || session.degraded) {
    return <Login session={session ?? null} />;
  }
  return <Authenticated session={session} />;
}

/** Una instancia de `Report` por clave de reporte, no una reutilizada. */
function ReportRoute({ config, session }: { config: Config; session: SessionInfo }) {
  const { reportKey } = useParams();
  return <Report key={reportKey} config={config} session={session} />;
}

function Authenticated({ session }: { session: SessionInfo }) {
  const { data: config, error, loading, reload } = useAsync(() => api.config(), []);

  /* Los colores que el administrador guardó, aplicados antes de pintar nada.
     Va en un efecto y no en el render porque escribe en `document.head`, que
     está fuera de React; y sin limpieza al desmontar, porque `Authenticated`
     solo desaparece cuando cae la sesión entera y quitar la hoja justo entonces
     solo serviría para que el portón parpadeara de color. */
  useEffect(() => {
    aplicarApariencia(config?.appearance);
  }, [config?.appearance]);

  /* `!config` y no solo `loading`.
     
     `onConfigStale` vuelve a pedir la configuración después de cada escritura
     —hace falta: el token CSRF es de un solo uso—. Con la condición anterior,
     esa recarga ponía `loading` en cierto y ESTA rama sustituía la aplicación
     entera por «Cargando la configuración»: la barra lateral, la cabecera y la
     tabla desaparecían y volvían. Eso era el parpadeo al pulsar «Sincronizar
     ahora», y ocurría igual al guardar el membrete o los colores.
     
     En una recarga ya hay configuración que enseñar, y lo que cambia de ella
     —un token que el usuario no ve— no justifica desmontar la pantalla. */
  if (loading && !config) return <Loading what="la configuración" />;
  if (error) {
    // La sesión pudo caducar entre una llamada y otra: se vuelve al portón en
    // vez de dejar una interfaz a medias con errores por todas partes.
    if (error instanceof ApiError && (error.isUnauthenticated || error.isDegraded)) {
      return (
        <Login
          session={{ ...session, authenticated: false, degraded: error.isDegraded }}
          /* A session existed and stopped being valid — something only the
             client can tell, since the provider never redirected here. */
          expired={error.isUnauthenticated}
        />
      );
    }
    return (
      <ErrorState
        error={error}
        hint="Si el error es 403, su rol no alcanza para esta pantalla."
      />
    );
  }
  if (!config) return null;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout config={config} session={session} />}>
          <Route index element={<Dashboard config={config} />} />
          <Route path="runs" element={<Runs config={config} onConfigStale={reload} />} />
          <Route path="runs/:runId" element={<RunDetail />} />
          <Route path="exceptions" element={<Exceptions />} />
          {/* Auditoría del mapping: cinco vistas de una misma pregunta —cómo
              se construye el estado—, incluidas las dos conciliaciones técnicas
              que antes se ofrecían en el menú con el nombre del documento
              oficial. */}
          <Route path="audit" element={<Audit config={config} session={session} />}>
            <Route index element={<AuditComposition />} />
            <Route path="divergences" element={<AuditDivergences />} />
            <Route path="rules" element={<AuditRules />} />
            <Route path="classification" element={<AuditClassification />} />
            <Route
              path="esf"
              element={
                <Statement
                  statementKey="statement_financial_position"
                  reportKey="statement_financial_position"
                  official="ESF"
                />
              }
            />
            <Route
              path="eri"
              element={
                <Statement
                  statementKey="income_statement"
                  reportKey="income_statement"
                  official="ERI"
                />
              }
            />
          </Route>
          <Route path="settings" element={<Settings />} />
          {/* Las rutas anteriores siguen funcionando: los enlaces guardados y
              los marcadores no se rompen porque una pantalla cambie de sitio. */}
          <Route
            path="statements/statement_financial_position"
            element={<Navigate to="/audit/esf" replace />}
          />
          <Route
            path="statements/income_statement"
            element={<Navigate to="/audit/eri" replace />}
          />
          {/* Cierre y estados financieros. La ruta de layout pide el paquete
              UNA vez y lo reparte por contexto: si cada pantalla se alimentara
              por su cuenta, volveríamos a tener un tablero dividiendo entre una
              cifra que el estado de al lado no reconoce. */}
          <Route path="closing" element={<ClosingLayout config={config} session={session} />}>
            <Route index element={<ClosingOverview />} />
            <Route path="dashboard" element={<FinancialDashboard />} />
            <Route path="esf" element={<FormalStatement report="esf" />} />
            <Route path="annex" element={<StatementAnnex />} />
            <Route path="eri" element={<FormalStatement report="eri" />} />
            <Route path="monthly" element={<MonthlyDetail />} />
            {/* El detalle por cuenta y la conciliación mensual son vistas
                DENTRO del estado, con ruta propia: la pestaña se comparte por
                enlace y responde a atrás/adelante, que es lo que un `useState`
                no daría. */}
            <Route path="equity" element={<EquityMatrix />}>
              <Route index element={<EquityOfficial />} />
              <Route path="detail" element={<EquityDetail />} />
            </Route>
            <Route path="cash-flow" element={<CashFlowStatement />}>
              <Route index element={<CashFlowOfficial />} />
              <Route path="detail" element={<CashFlowDetail />} />
              <Route path="reconciliation" element={<CashFlowReconciliation />} />
            </Route>
            <Route path="ratios" element={<Ratios />} />
            <Route path="notes" element={<ClosingNotes />} />
            <Route path="policy" element={<AccountingPolicy />} />
          </Route>
          {/* `ReportRoute` remonta la pantalla al cambiar de reporte: el estado
              inicial de la vista —jerarquía o tabla— depende del reporte, y sin
              remontar se arrastraría el del anterior. */}
          {/* Antes de la ruta genérica: el comprobante no es una tabla, es
              una lista de documentos y tiene pantalla propia. */}
          <Route path="reports/journal_voucher" element={<JournalVoucherPage />} />
          <Route path="reports/:reportKey" element={<ReportRoute config={config} session={session} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
